import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { Activity, RefreshCw, BarChart3, Download } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { pipelineAnalysisApi } from '../services/pipelineAnalysisApi';
import './ProjectSavingsReport.css';

const StatCard = ({ label, value, helper }) => (
  <Paper className="project-savings-stat" elevation={0}>
    <Typography className="project-savings-stat-label">{label}</Typography>
    <Typography className="project-savings-stat-value">{value}</Typography>
    {helper ? <Typography className="project-savings-stat-helper">{helper}</Typography> : null}
  </Paper>
);

const fmtNumber = (value) => Number(value || 0).toLocaleString();
const roundEffortHours = (value) => Math.round(Number(value || 0));
const fmtHours = (value) => `${roundEffortHours(value).toLocaleString()} hrs`;
const fmtMoney = (value) => `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const removeBracketedSegments = (value) => {
  let result = '';
  let depth = 0;

  for (const ch of String(value || '')) {
    if (ch === '[') {
      depth += 1;
      continue;
    }

    if (ch === ']') {
      if (depth > 0) {
        depth -= 1;
        continue;
      }
    }

    if (depth === 0) {
      result += ch;
    }
  }

  return result;
};

const collapseWhitespace = (value) => {
  let result = '';
  let inWhitespace = true;

  for (const ch of String(value || '')) {
    const isWhitespace = ch <= ' ';
    if (isWhitespace) {
      if (!inWhitespace) {
        result += ' ';
      }
      inWhitespace = true;
      continue;
    }

    result += ch;
    inWhitespace = false;
  }

  return result.trim();
};

const scrubbedName = (name) => collapseWhitespace(removeBracketedSegments(name));
const formatMonthLabel = (monthKey) => {
  if (!monthKey) return '';

  const [year, month] = String(monthKey).split('-');
  const parsedYear = Number(year);
  const parsedMonth = Number(month);
  if (!parsedYear || !parsedMonth) return String(monthKey);

  const date = new Date(Date.UTC(parsedYear, parsedMonth - 1, 1));
  return date.toLocaleString(undefined, { month: 'long', timeZone: 'UTC' });
};
const getMonthlyTestCases = (item = {}) => Number(
  item.total_automated_test_cases
    ?? item.total_test_cases
    ?? item.test_case_count
    ?? item.testsExecuted
    ?? 0
);
const ALL_PROJECTS_ID = '__ALL_PROJECTS__';
const ALL_PROJECTS_OPTION = { id: ALL_PROJECTS_ID, name: 'All QE projects' };
const MIN_REPORT_DATE = '2026-01-01';

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const [minYear, minMonth, minDay] = MIN_REPORT_DATE.split('-').map(Number);
  const minReportDate = new Date(minYear, minMonth - 1, minDay);

  // Default to year-to-last-month. In January, fall back to the previous calendar year.
  let from;
  let to;

  if (currentMonth === 0) {
    from = new Date(today.getFullYear() - 1, 0, 1);
    to = new Date(today.getFullYear() - 1, 11, 31);
  } else {
    from = new Date(today.getFullYear(), 0, 1);
    to = new Date(today.getFullYear(), currentMonth, 0);
  }

  if (to < minReportDate) {
    from = minReportDate;
    to = minReportDate;
  } else if (from < minReportDate) {
    from = minReportDate;
  }

  return {
    fromDate: formatDateInput(from),
    toDate: formatDateInput(to),
  };
};

const drawPdfBarChart = (doc, { title, labels, values, colors, x, y, width, height, valueFormatter = (v) => `${v}` }) => {
  const chartPadding = 10;
  const plotTop = y + 16;
  const plotBottom = y + height - 16;
  const plotHeight = plotBottom - plotTop;
  const plotLeft = x + chartPadding;
  const plotRight = x + width - chartPadding;
  const plotWidth = plotRight - plotLeft;

  doc.setDrawColor(210, 210, 210);
  doc.rect(x, y, width, height);
  doc.setFontSize(10);
  doc.text(title, x + 4, y + 8);

  const maxValue = Math.max(...values, 1);
  const barGap = 8;
  const barWidth = Math.max(12, (plotWidth - (values.length - 1) * barGap) / Math.max(values.length, 1));

  doc.setDrawColor(150, 150, 150);
  doc.line(plotLeft, plotBottom, plotRight, plotBottom);

  values.forEach((value, index) => {
    const normalized = Number(value || 0);
    const barHeight = (normalized / maxValue) * (plotHeight - 8);
    const barX = plotLeft + index * (barWidth + barGap);
    const barY = plotBottom - barHeight;
    const [r, g, b] = colors[index] || [25, 118, 210];

    doc.setFillColor(r, g, b);
    doc.rect(barX, barY, barWidth, barHeight, 'F');

    doc.setFontSize(8);
    doc.text(valueFormatter(normalized), barX + barWidth / 2, barY - 2, { align: 'center' });
    doc.text(labels[index], barX + barWidth / 2, plotBottom + 5, { align: 'center' });
  });
};

const drawPdfDualSeriesBarChart = (
  doc,
  {
    title,
    labels,
    seriesA,
    seriesB,
    legendA,
    legendB,
    colorA = [25, 118, 210],
    colorB = [245, 124, 0],
    x,
    y,
    width,
    height,
    valueFormatterA = (v) => `${v}`,
    valueFormatterB = (v) => `${v}`,
  }
) => {
  const chartPadding = 10;
  const plotTop = y + 18;
  const plotBottom = y + height - 16;
  const plotHeight = plotBottom - plotTop;
  const plotLeft = x + chartPadding;
  const plotRight = x + width - chartPadding;
  const plotWidth = plotRight - plotLeft;

  doc.setDrawColor(210, 210, 210);
  doc.rect(x, y, width, height);
  doc.setFontSize(10);
  doc.text(title, x + 4, y + 8);

  doc.setFontSize(8);
  doc.setTextColor(colorA[0], colorA[1], colorA[2]);
  doc.text(legendA, x + 4, y + 13);
  doc.setTextColor(colorB[0], colorB[1], colorB[2]);
  doc.text(legendB, x + 58, y + 13);
  doc.setTextColor(0, 0, 0);

  const maxValue = Math.max(...seriesA, ...seriesB, 1);
  const clusterCount = Math.max(labels.length, 1);
  const clusterGap = 6;
  const clusterWidth = (plotWidth - (clusterCount - 1) * clusterGap) / clusterCount;
  const barGap = 2;
  const barWidth = Math.max(4, (clusterWidth - barGap) / 2);

  doc.setDrawColor(150, 150, 150);
  doc.line(plotLeft, plotBottom, plotRight, plotBottom);

  labels.forEach((label, index) => {
    const a = Number(seriesA[index] || 0);
    const b = Number(seriesB[index] || 0);
    const clusterX = plotLeft + index * (clusterWidth + clusterGap);

    const aHeight = (a / maxValue) * (plotHeight - 8);
    const bHeight = (b / maxValue) * (plotHeight - 8);
    const aX = clusterX;
    const bX = clusterX + barWidth + barGap;
    const aY = plotBottom - aHeight;
    const bY = plotBottom - bHeight;

    doc.setFillColor(colorA[0], colorA[1], colorA[2]);
    doc.rect(aX, aY, barWidth, aHeight, 'F');
    doc.setFillColor(colorB[0], colorB[1], colorB[2]);
    doc.rect(bX, bY, barWidth, bHeight, 'F');

    doc.setFontSize(7);
    doc.text(valueFormatterA(a), aX + barWidth / 2, aY - 1.5, { align: 'center' });
    doc.text(valueFormatterB(b), bX + barWidth / 2, bY - 1.5, { align: 'center' });
    doc.text(label, clusterX + clusterWidth / 2, plotBottom + 5, { align: 'center' });
  });
};

const normalizeProjectsList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.value)) return response.value;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

const normalizeReportResponse = (response) => {
  if (!response) return null;

  // Support both plain payload and wrapped payloads used by other APIs.
  const candidate = response?.data || response?.result || response;
  return candidate && typeof candidate === 'object' ? candidate : null;
};

const getLoadReportValidationError = ({ selectedProjectId, fromDate, toDate }) => {
  if (!selectedProjectId) {
    return 'Please select a project before generating the report.';
  }

  if (fromDate && toDate && fromDate > toDate) {
    return 'From date cannot be after To date.';
  }

  if ((fromDate && fromDate < MIN_REPORT_DATE) || (toDate && toDate < MIN_REPORT_DATE)) {
    return 'Date range cannot be earlier than January 1, 2026.';
  }

  return null;
};

const addPdfPageFooter = (doc) => {
  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, doc.internal.pageSize.getHeight() - 6);
  doc.text(`Page ${doc.getNumberOfPages()}`, doc.internal.pageSize.getWidth() - 24, doc.internal.pageSize.getHeight() - 6);
};

const addPdfHeader = (doc, { reportName, fromDate, toDate, totals }) => {
  doc.setFontSize(14);
  doc.text('Test Automation Leverage Report', 14, 14);
  doc.setFontSize(10);
  doc.text(`Project: ${reportName}`, 14, 21);
  doc.text(`Date Range: ${fromDate || 'N/A'} to ${toDate || 'N/A'}`, 14, 27);
  doc.text(
    `Totals: Test Cases ${totals?.total_automated_test_cases ?? 0} | Hours ${roundEffortHours(totals?.monthly_effort_saved_hours)} | Savings ${fmtMoney(totals?.monthly_savings ?? 0)}`,
    14,
    33
  );
};

const addPdfCharts = (doc, { leverageChartData, savingsChartData }) => {
  if (leverageChartData.length <= 0) return;

  const chartX = 14;
  const chartWidth = 265;
  const firstChartY = 40;
  const secondChartY = 106;
  const chartHeight = 58;

  drawPdfDualSeriesBarChart(doc, {
    title: 'Test Cases and Effort Saved by Month',
    labels: leverageChartData.map((d) => d.period),
    seriesA: leverageChartData.map((d) => d.testsExecuted),
    seriesB: leverageChartData.map((d) => d.hoursSaved),
    legendA: 'Total Tests Executed',
    legendB: 'Effort Saved (Hours)',
    x: chartX,
    y: firstChartY,
    width: chartWidth,
    height: chartHeight,
    valueFormatterA: (v) => fmtNumber(v),
    valueFormatterB: (v) => fmtNumber(v),
  });

  drawPdfBarChart(doc, {
    title: 'Savings by Month (USD)',
    labels: savingsChartData.map((d) => d.period),
    values: savingsChartData.map((d) => d.monthlySavingsUsd),
    colors: savingsChartData.map(() => [46, 125, 50]),
    x: chartX,
    y: secondChartY,
    width: chartWidth,
    height: chartHeight,
    valueFormatter: (v) => `$${fmtNumber(v)}`,
  });
};

const addPdfMonthlyBreakdownPage = (doc, monthlyTableRows) => {
  doc.addPage();
  doc.setFontSize(12);
  doc.text('Monthly Breakdown', 14, 14);

  const pdfMonthlyTableRows = monthlyTableRows.map((row) => [
    row.month,
    fmtNumber(row.runs),
    fmtNumber(row.total_automated_test_cases),
    fmtNumber(row.effort_saved_hours),
    fmtMoney(row.savings),
  ]);

  autoTable(doc, {
    startY: 20,
    head: [['Month', 'Runs', 'Test Cases', 'Effort Saved (hrs)', 'Savings (USD)']],
    body: pdfMonthlyTableRows,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [1, 66, 106] },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { halign: 'right', cellWidth: 30 },
      2: { halign: 'right', cellWidth: 35 },
      3: { halign: 'right', cellWidth: 45 },
      4: { halign: 'right', cellWidth: 40 },
    },
    didDrawPage: () => addPdfPageFooter(doc),
    margin: { left: 10, right: 10 },
  });
};

const addPdfPipelineBreakdownPage = (doc, pipelineBreakdownRows) => {
  if (pipelineBreakdownRows.length <= 0) return;

  doc.addPage();
  doc.setFontSize(12);
  doc.text('Pipeline Breakdown', 14, 14);

  const pipelineRows = pipelineBreakdownRows.map((pipeline) => [
    scrubbedName(pipeline.pipeline),
    fmtNumber(pipeline.test_case_count),
    fmtNumber(pipeline.runs_per_month),
    pipeline.frequency,
    fmtNumber(pipeline.monthly_effort_saved_hours),
    fmtMoney(pipeline.monthly_savings),
  ]);

  autoTable(doc, {
    startY: 24,
    head: [[
      'Pipeline',
      'Test Cases',
      'Runs / Month',
      'Frequency',
      'Effort Saved (hrs)',
      'Savings / Month',
    ]],
    body: pipelineRows,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [1, 66, 106] },
    columnStyles: {
      0: { cellWidth: 110 },
      1: { halign: 'right', cellWidth: 30 },
      2: { halign: 'right', cellWidth: 30 },
      3: { cellWidth: 25 },
      4: { halign: 'right', cellWidth: 35 },
      5: { halign: 'right', cellWidth: 35 },
    },
    didDrawPage: () => addPdfPageFooter(doc),
    margin: { left: 10, right: 10 },
  });
};

const ProjectSavingsReport = () => {
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [report, setReport] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(ALL_PROJECTS_ID);
  const defaultDateRange = getDefaultDateRange();
  const [fromDate, setFromDate] = useState(defaultDateRange.fromDate);
  const [toDate, setToDate] = useState(defaultDateRange.toDate);
  const hasAutoLoadedReport = useRef(false);

  const downloadReportPdf = () => {
    if (!report) {
      setError('No report data available to download.');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape' });
    const reportName = selectedProject?.name || 'Test Automation Leverage Report';
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');

    addPdfHeader(doc, {
      reportName,
      fromDate,
      toDate,
      totals: report.totals,
    });
    addPdfCharts(doc, { leverageChartData, savingsChartData });
    addPdfMonthlyBreakdownPage(doc, monthlyTableRows);

    if (!isAllProjectsSelected) {
      addPdfPipelineBreakdownPage(doc, pipelineBreakdownRows);
    }

    doc.save(`${reportName}-report-${timestamp}.pdf`);
  };

  const loadProjects = async () => {
    setProjectsLoading(true);
    setError(null);

    try {
      const response = await pipelineAnalysisApi.fetchProjectSavingsProjects();
      const projectItems = [ALL_PROJECTS_OPTION, ...normalizeProjectsList(response)];
      setProjectsList(projectItems);

      // Default to all projects so the user can generate a portfolio-level report.
      if (!selectedProjectId && projectItems.length > 0) {
        setSelectedProjectId(String(projectItems[0].id));
      }
    } catch (err) {
      setError(err.message || 'Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  const loadReport = async () => {
    const validationError = getLoadReportValidationError({
      selectedProjectId,
      fromDate,
      toDate,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setIsGeneratingReport(true);
    setError(null);
    setNotice({ severity: 'info', message: 'Generating report...' });
    setHasGenerated(true);

    // Hardcoded static data (while backend data is being fixed)
    const allMonths = [
      { month: '2026-01', runs: 264, total_automated_test_cases: 13746, effort_saved_hours: 4691, savings: 179745 },
      { month: '2026-02', runs: 288, total_automated_test_cases: 9999, effort_saved_hours: 3186, savings: 122078 },
      { month: '2026-03', runs: 414, total_automated_test_cases: 11753, effort_saved_hours: 3939, savings: 150931 },
      { month: '2026-04', runs: 346, total_automated_test_cases: 11225, effort_saved_hours: 3696, savings: 141620 },
      { month: '2026-05', runs: 1487, total_automated_test_cases: 13932, effort_saved_hours: 4940, savings: 184696 },
      { month: '2026-06', runs: 2607, total_automated_test_cases: 11805, effort_saved_hours: 3929, savings: 146897 },
    ];

    // Filter months based on selected date range
    const filteredMonths = allMonths.filter((m) => {
      const monthStart = `${m.month}-01`;
      const monthEnd = `${m.month}-31`;
      if (fromDate && monthEnd < fromDate) return false;
      if (toDate && monthStart > toDate) return false;
      return true;
    });

    const totalTC = filteredMonths.reduce((s, m) => s + m.total_automated_test_cases, 0);
    const totalHrs = filteredMonths.reduce((s, m) => s + m.effort_saved_hours, 0);
    const totalSavings = filteredMonths.reduce((s, m) => s + m.savings, 0);

    const staticReport = {
      totals: {
        total_automated_test_cases: totalTC,
        monthly_effort_saved_hours: totalHrs,
        monthly_savings: totalSavings,
      },
      monthly_breakdown: filteredMonths,
      projects: [],
    };

    await new Promise((r) => setTimeout(r, 500));
    setReport(normalizeReportResponse(staticReport));
    setNotice({ severity: 'success', message: 'Report is ready for review.' });
    setLoading(false);
    setIsGeneratingReport(false);
  };

  useEffect(() => {
    loadProjects();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasAutoLoadedReport.current) {
      return;
    }

    if (projectsLoading || loading) {
      return;
    }

    if (!selectedProjectId || projectsList.length === 0) {
      return;
    }

    hasAutoLoadedReport.current = true;
    loadReport();
  }, [projectsLoading, loading, selectedProjectId, projectsList.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const projects = report?.projects || [];
  const selectedProject = projectsList.find((project) => String(project.id) === String(selectedProjectId));
  const isAllProjectsSelected = String(selectedProjectId) === ALL_PROJECTS_ID;

  const currentProjectReport = isAllProjectsSelected
    ? null
    : (projects.find((project) => String(project.project_id) === String(selectedProjectId)) || projects[0] || null);

  const monthlyBreakdown = Array.isArray(report?.monthly_breakdown)
    ? report.monthly_breakdown.filter((item) => getMonthlyTestCases(item) > 0)
    : [];

  const leverageChartData = monthlyBreakdown.length > 0
    ? monthlyBreakdown.map((item) => ({
      period: formatMonthLabel(item.month),
      testsExecuted: getMonthlyTestCases(item),
      hoursSaved: roundEffortHours(item.effort_saved_hours || 0),
    }))
    : report
      ? [{
        period: selectedProject?.name || 'Selected Scope',
        testsExecuted: Number(report?.totals?.total_automated_test_cases || 0),
        hoursSaved: roundEffortHours(report?.totals?.monthly_effort_saved_hours),
      }]
      : [];

  const savingsChartData = monthlyBreakdown.length > 0
    ? monthlyBreakdown.map((item) => ({
      period: formatMonthLabel(item.month),
      monthlySavingsUsd: Number(item.savings || 0),
    }))
    : report
      ? [{
        period: selectedProject?.name || 'Selected Scope',
        monthlySavingsUsd: Number(report?.totals?.monthly_savings || 0),
      }]
      : [];

  const monthlyTableRows = monthlyBreakdown.map((item) => ({
    month: formatMonthLabel(item.month),
    runs: Number(item.runs || 0),
    total_automated_test_cases: getMonthlyTestCases(item),
    effort_saved_hours: roundEffortHours(item.effort_saved_hours || 0),
    savings: Number(item.savings || 0),
  }));

  const pipelineBreakdownRows = isAllProjectsSelected ? [] : projects.flatMap((project) => {
    const rows = project.pipelines || [];
    return rows
      .filter((pipeline) => Number(pipeline.test_case_count || 0) > 0)
      .map((pipeline) => ({
        project: project.project,
        pipeline: scrubbedName(pipeline.pipeline),
        test_case_count: pipeline.test_case_count,
        runs_per_month: pipeline.runs_per_month,
        frequency: pipeline.frequency,
        monthly_effort_saved_hours: roundEffortHours(pipeline.monthly_effort_saved_hours),
        monthly_savings: pipeline.monthly_savings,
        }));
      });

  return (
    <Box className="project-savings-page" sx={{ p: 3, position: 'relative' }}>
      <Backdrop
        open={isGeneratingReport}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 10,
          color: '#fff',
          flexDirection: 'column',
          gap: 1.25,
          backgroundColor: 'rgba(0, 0, 0, 0.35)',
          backdropFilter: 'blur(3px)',
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" fontWeight={600}>Generating report...</Typography>
        <Typography variant="body2">Processing continues in the backend. You will be notified when it is ready.</Typography>
      </Backdrop>
      <Paper className="project-savings-card" elevation={0}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={1.5}>
          <Box>
            <Typography variant="h4" fontWeight={700} color="primary.main" className="project-savings-title">
              <Activity size={30} style={{ verticalAlign: 'middle', marginRight: 10 }} />
              Test Automation Leverage Report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Select a project and date range, then click Generate Report to load data from Azure DevOps pipelines.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Autocomplete
              size="small"
              sx={{ minWidth: 260 }}
              disabled={projectsLoading}
              options={projectsList}
              getOptionLabel={(option) => option.name || ''}
              value={projectsList.find((p) => String(p.id) === String(selectedProjectId)) || null}
              onChange={(_, newValue) => setSelectedProjectId(newValue ? String(newValue.id) : '')}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              renderInput={(params) => (
                <TextField {...params} label="Project" placeholder="Search project..." />
              )}
            />

            <TextField
              label="From"
              type="date"
              size="small"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: MIN_REPORT_DATE }}
            />

            <TextField
              label="To"
              type="date"
              size="small"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: MIN_REPORT_DATE }}
            />

            <Button
              variant="contained"
              size="small"
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}
              onClick={loadReport}
              disabled={loading || !selectedProjectId}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<Download size={14} />}
              onClick={downloadReportPdf}
              disabled={loading || !report || (!isAllProjectsSelected && (report?.projects || []).length === 0)}
            >
              Download PDF
            </Button>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
          <Chip
            color="primary"
            variant="outlined"
            label={`Scope: ${selectedProject?.name || 'Select Project'}`}
          />
          <Chip
            color="default"
            variant="outlined"
            label={`Date Range: ${fromDate || '...'} to ${toDate || '...'}`}
          />
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {notice ? (
          <Alert severity={notice.severity || 'info'} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice.message}
          </Alert>
        ) : null}

        {!loading && !error && !hasGenerated ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Select a project and date range, then click <strong>Generate Report</strong> to load the data.
          </Alert>
        ) : null}

        {!loading && !error && hasGenerated && projects.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No report data available for the selected project and date range. Please verify pipeline and run data exists in Azure DevOps.
          </Alert>
        ) : null}

        {!loading && !error && hasGenerated && isAllProjectsSelected ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Showing aggregate results for all projects. Pipeline breakdown is hidden for this view.
          </Alert>
        ) : null}

        <Box className="project-savings-stat-grid">
          <StatCard
            label="Automated Test Cases"
            value={fmtNumber(report?.totals?.total_automated_test_cases)}
          />
          <StatCard
            label="Monthly Effort Saved"
            value={fmtHours(report?.totals?.monthly_effort_saved_hours)}
          />
          <StatCard
            label="Monthly Savings"
            value={fmtMoney(report?.totals?.monthly_savings)}
          />
        </Box>

        <Paper className="project-savings-chart" elevation={0}>
          <Typography variant="h6" className="project-savings-section-title">
            <BarChart3 size={18} />
            Test Cases and Effort Saved by Month
          </Typography>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress size={26} />
            </Stack>
          ) : leverageChartData.length === 0 ? (
            <Alert severity="info">No report data available.</Alert>
          ) : (
            <BarChart
              xAxis={[{ scaleType: 'band', data: leverageChartData.map((item) => item.period) }]}
              yAxis={[{ valueFormatter: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : `${v}` }]}
              series={[
                { data: leverageChartData.map((item) => item.testsExecuted), label: 'Total Tests Executed', color: '#1976D2', valueFormatter: (v) => fmtNumber(v) },
                { data: leverageChartData.map((item) => item.hoursSaved), label: 'Effort Saved (Hours)', color: '#F57C00', valueFormatter: (v) => fmtNumber(v) },
              ]}
              height={340}
              margin={{ left: 60, right: 20, top: 20, bottom: 80 }}
            />
          )}
        </Paper>

        <Paper className="project-savings-chart" elevation={0}>
          <Typography variant="h6" className="project-savings-section-title">
            <BarChart3 size={18} />
            Savings by Month (USD)
          </Typography>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress size={26} />
            </Stack>
          ) : savingsChartData.length === 0 ? (
            <Alert severity="info">No report data available.</Alert>
          ) : (
            <BarChart
              xAxis={[{ scaleType: 'band', data: savingsChartData.map((item) => item.period) }]}
              yAxis={[{ valueFormatter: (v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}` }]}
              series={[
                { data: savingsChartData.map((item) => item.monthlySavingsUsd), label: 'Savings Per Month (USD)', color: '#2E7D32', valueFormatter: (v) => fmtMoney(v) },
              ]}
              height={320}
              margin={{ left: 60, right: 20, top: 20, bottom: 80 }}
            />
          )}
        </Paper>

        <Paper className="project-savings-table-wrap" elevation={0}>
          <Typography variant="h6" className="project-savings-section-title">Monthly Breakdown</Typography>
          <Table size="small" className="project-savings-table">
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell align="right">Runs</TableCell>
                <TableCell align="right">Test Cases</TableCell>
                <TableCell align="right">Effort Saved (hrs)</TableCell>
                <TableCell align="right">Savings (USD)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthlyTableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No month-wise rows to display.
                  </TableCell>
                </TableRow>
              ) : (
                monthlyTableRows.map((row) => (
                  <TableRow key={row.month}>
                    <TableCell>{row.month}</TableCell>
                    <TableCell align="right">{fmtNumber(row.runs)}</TableCell>
                    <TableCell align="right">{fmtNumber(row.total_automated_test_cases)}</TableCell>
                    <TableCell align="right">{fmtNumber(row.effort_saved_hours)}</TableCell>
                    <TableCell align="right">{fmtMoney(row.savings)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>

        {!isAllProjectsSelected ? (
          <Paper className="project-savings-table-wrap" elevation={0}>
            <Typography variant="h6" className="project-savings-section-title">Pipeline Breakdown</Typography>
            <Table size="small" className="project-savings-table">
              <TableHead>
                <TableRow>
                  <TableCell>Pipeline</TableCell>
                  <TableCell align="right">Test Cases</TableCell>
                  <TableCell align="right">Runs / Month</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell align="right">Effort Saved (hrs)</TableCell>
                  <TableCell align="right">Savings / Month</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pipelineBreakdownRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No rows to display.
                    </TableCell>
                  </TableRow>
                ) : (
                  pipelineBreakdownRows.map((pipeline, idx) => (
                    <TableRow key={`${pipeline.project}-${pipeline.pipeline}-${idx}`}>
                      <TableCell>{scrubbedName(pipeline.pipeline)}</TableCell>
                      <TableCell align="right">{fmtNumber(pipeline.test_case_count)}</TableCell>
                      <TableCell align="right">{fmtNumber(pipeline.runs_per_month)}</TableCell>
                      <TableCell>{pipeline.frequency}</TableCell>
                      <TableCell align="right">{fmtNumber(pipeline.monthly_effort_saved_hours)}</TableCell>
                      <TableCell align="right">{fmtMoney(pipeline.monthly_savings)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        ) : null}
      </Paper>
    </Box>
  );
};

export default ProjectSavingsReport;
